import type { AnnouncerAudioEngine } from '@src/audio/AnnouncerAudioEngine';
import type { CrowdBed } from '@src/audio/CrowdBed';
import type { MusicEngine } from '@src/audio/MusicEngine';
import { type Sfx, SfxId } from '@src/audio/Sfx';

export type OpeningCut = 'full' | 'short' | 'none';

export interface CinematicAudio {
  announcer: AnnouncerAudioEngine;
  sfx: Sfx;
  music: MusicEngine;
  crowd: CrowdBed;
}

export interface OpeningCinematicOptions {
  /** Element the scene mounts into (the boot layer); removed again when the cut ends. */
  host: HTMLElement;
  audio: CinematicAudio;
  cut: Exclude<OpeningCut, 'none'>;
}

const SEEN_KEY = 'wa.intro.seen';
const ART_PATH = '/assets/ui';
const PANELS = ['studio', 'tower', 'host', 'contestant'] as const;
type PanelName = (typeof PANELS)[number];
type KenBurns = 'a' | 'b' | 'c';

/** Any input this soon after the start press is the same press; ignore it. */
const ARM_SKIP_MS = 650;
/** ~24 cps — readable and near spoken cadence (was 13ms, an unreadable blur). */
const TYPE_MS_PER_CHAR = 42;
/** Pause between crawl lines while narration continues. */
const CRAWL_LINE_HOLD_MS = 720;
/** Breathing room after a lore clip ends before the next block. */
const LORE_BREATH_MS = 1000;
/** If a lore clip is missing, still dwell after typing so the block is readable. */
const LORE_FALLBACK_HOLD_MS = 2800;
/** Hard ceiling per voiced beat if a clip is missing or the decode hangs. */
const VOICE_MAX_MS = 16000;
/** Lore blocks are longer narrations; give them more runway. */
const LORE_VOICE_MAX_MS = 20000;

const CRAWL_A = ['Every night, the seven realms tune in to the Arcane Broadcast.', 'Beneath the Tower: the Grand Arena. Portals open. Fiends pour out. The crowd roars.'];
const CRAWL_B = ['The prizes are real. Gold. Relics. A lifetime supply of mana.', 'The small print is also real. Contestants rarely leave.'];
const STAKES = ['KILL THE FIENDS.', 'SMASH THE PORTALS.', 'SURVIVE THE BOSS.', 'WIN BIG.'];
/** Slam-in offsets that ride the drumroll: three on the roll, the last on its crash. */
const STAKE_SLAM_MS = [350, 780, 1210, 1720];

function readSeen(): boolean {
  try {
    return window.localStorage.getItem(SEEN_KEY) === '1';
  } catch {
    return false;
  }
}

function writeSeen(): void {
  try {
    window.localStorage.setItem(SEEN_KEY, '1');
  } catch {
    // Private mode or storage disabled: the player just gets the full cut again.
  }
}

/**
 * Which cut plays. `?intro=full|short|none` wins for testing; otherwise the
 * first visit gets the story and every visit after gets the short send-off.
 */
export function openingCut(): OpeningCut {
  const param = new URLSearchParams(window.location.search).get('intro');
  if (param === 'full' || param === 'short' || param === 'none') return param;
  return readSeen() ? 'short' : 'full';
}

/** Warm the backdrop cache while the renderer boots so panels never pop in late. */
export function prefetchOpeningArt(): void {
  for (const name of PANELS) {
    const img = new Image();
    img.decoding = 'async';
    img.src = `${ART_PATH}/intro_${name}.jpg`;
  }
}

function el<K extends keyof HTMLElementTagNameMap>(tag: K, className: string, parent?: HTMLElement): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  node.className = className;
  parent?.appendChild(node);
  return node;
}

/**
 * The Arcane Broadcast cold open. A DOM/CSS scene over the boot layer: four
 * Ken Burns backdrops, typewriter lore crawl, title slam, Vaelen's host beats
 * with subtitles, the stakes card, and the send-off. Resolves when the cut
 * ends or the player skips; the caller then runs the GATES ARE LOCKED slam.
 */
class OpeningCinematic {
  private readonly root: HTMLDivElement;
  private readonly panels = new Map<PanelName, HTMLDivElement>();
  private readonly text: HTMLDivElement;
  private readonly caption: HTMLDivElement;
  private readonly captionWho: HTMLSpanElement;
  private readonly captionText: HTMLSpanElement;
  private readonly flash: HTMLDivElement;
  private readonly wipe: HTMLDivElement;
  private readonly hint: HTMLDivElement;
  private readonly onair: HTMLDivElement;
  private readonly timers = new Set<number>();
  private readonly waiters: Array<() => void> = [];
  private readonly audio: CinematicAudio;
  private readonly cut: 'full' | 'short';
  private skipped = false;
  private finished = false;
  private raf = 0;
  private captionTimer = 0;
  private padButtons: boolean[] = [];
  private padArmed = false;
  private prevCaption: AnnouncerAudioEngine['onCaption'] = null;
  private resolveDone: (() => void) | null = null;

  constructor(opts: OpeningCinematicOptions) {
    this.audio = opts.audio;
    this.cut = opts.cut;
    this.root = el('div', 'cine');
    const stage = el('div', 'cine-stage', this.root);
    for (const name of PANELS) {
      const panel = el('div', `cine-panel art-${name}`, stage);
      el('div', 'cine-art', panel);
      const img = el('img', 'cine-img', panel);
      img.alt = '';
      img.decoding = 'async';
      img.src = `${ART_PATH}/intro_${name}.jpg`;
      img.addEventListener('error', () => img.remove(), { once: true });
      this.panels.set(name, panel);
    }
    el('div', 'cine-grain', stage);
    el('div', 'cine-vignette', stage);
    this.text = el('div', 'cine-text', this.root);
    this.caption = el('div', 'cine-caption', this.root);
    this.captionWho = el('span', 'who', this.caption);
    this.captionWho.textContent = 'ARCHMAGE VAELEN';
    this.captionText = el('span', 'line', this.caption);
    el('div', 'cine-bar top', this.root);
    el('div', 'cine-bar bottom', this.root);
    this.onair = el('div', 'cine-onair', this.root);
    el('i', 'ring', this.onair);
    el('i', 'dot', this.onair);
    const onairLabel = el('span', 'label', this.onair);
    onairLabel.textContent = 'ON AIR';
    this.hint = el('div', 'cine-skip', this.root);
    this.hint.textContent = 'PRESS ANY KEY TO SKIP';
    this.wipe = el('div', 'cine-wipe', this.root);
    this.flash = el('div', 'cine-flash', this.root);
    opts.host.appendChild(this.root);
  }

  play(): Promise<void> {
    return new Promise<void>((resolve) => {
      this.resolveDone = resolve;
      this.prevCaption = this.audio.announcer.onCaption;
      this.audio.announcer.onCaption = (text, ms) => this.showCaption(text, ms);
      this.tick();
      window.setTimeout(() => this.armSkip(), ARM_SKIP_MS);
      // Two frames so the mount paints at opacity 0 before the fade-in class lands.
      requestAnimationFrame(() => requestAnimationFrame(() => this.root.classList.add('show')));
      void this.run();
    });
  }

  private async run(): Promise<void> {
    const beats = this.cut === 'short' ? this.shortBeats() : this.fullBeats();
    for (const beat of beats) {
      if (this.skipped) break;
      await beat();
    }
    this.finish();
  }

  // ---- timelines -------------------------------------------------------

  private fullBeats(): Array<() => Promise<void>> {
    const { sfx, music } = this.audio;
    return [
      async () => {
        // Cold open: the sigil lights, the crowd murmurs, studio from above.
        sfx.play(SfxId.OnAir, 0.9);
        this.onair.classList.add('live');
        music.setIntroBuild(0.1);
        this.audio.crowd.setMurmur(0.13, 0.7);
        await this.wait(500);
        this.showPanel('studio', 'a');
        this.audio.crowd.sting('cheer', 0.45);
        await this.wait(350);
        this.setKicker('THE ARCANE BROADCAST');
        await this.wait(550);
        await this.crawlNarrated(CRAWL_A, 'intro_lore_1');
        this.showHint();
      },
      async () => {
        await this.runeWipe('tower', 'b');
        this.clearText();
        this.setKicker('LIVE FROM THE GRAND ARENA');
        music.setIntroBuild(0.3);
        await this.wait(400);
        await this.crawlNarrated(CRAWL_B, 'intro_lore_2');
      },
      async () => {
        // Title slam on the tower, then Vaelen starts talking under it.
        this.clearText();
        await this.wait(200);
        this.showTitle();
        sfx.play(SfxId.OrchestraHit, 1);
        this.doFlash();
        music.setIntroBuild(0.5);
        await this.wait(1150);
      },
      async () => {
        const said = this.say('intro_welcome');
        this.audio.crowd.sting('cheer', 0.72, 0.25);
        await this.wait(1000);
        await this.runeWipe('host', 'c');
        this.clearText();
        this.setKicker('YOUR HOST', 'plate');
        await this.waitVoice(said, 2600);
      },
      async () => {
        await this.runeWipe('contestant', 'a');
        this.setKicker("TONIGHT'S CONTESTANT", 'plate');
        music.setIntroBuild(0.62);
        const said = this.say('intro_contestant');
        this.audio.crowd.sting('applause', 0.7, 0.4);
        await this.waitVoice(said, 2600);
      },
      async () => {
        // Drumroll starts under the wipe so the first slam lands as the studio settles.
        sfx.play(SfxId.Drumroll, 0.9);
        await this.runeWipe('studio', 'b');
        this.clearText();
        music.setIntroBuild(0.8);
        await this.stakesCard(560);
        const said = this.say('intro_stakes');
        await this.waitVoice(said, 2200);
      },
      async () => {
        await this.runeWipe('host', 'a');
        this.clearText();
        this.setKicker('AND NOW');
        music.setIntroBuild(1);
        const said = this.say('intro_sendoff');
        this.audio.crowd.sting('cheerBig', 0.95, 0.6, true);
        await this.waitVoice(said, 1800);
        await this.wait(250);
      },
    ];
  }

  private shortBeats(): Array<() => Promise<void>> {
    const { sfx, music } = this.audio;
    return [
      async () => {
        sfx.play(SfxId.OnAir, 0.9);
        this.onair.classList.add('live');
        this.showHint();
        this.audio.crowd.setMurmur(0.13, 0.6);
        music.setIntroBuild(0.35);
        await this.wait(450);
        this.showPanel('studio', 'c');
        await this.wait(300);
        this.showTitle();
        sfx.play(SfxId.OrchestraHit, 1);
        this.doFlash();
        await this.wait(1250);
      },
      async () => {
        await this.runeWipe('host', 'a');
        this.clearText();
        this.setKicker('YOUR HOST, ARCHMAGE VAELEN', 'plate');
        music.setIntroBuild(1);
        const said = this.say('intro_sendoff');
        this.audio.crowd.sting('cheerBig', 0.85, 0.4, true);
        await this.waitVoice(said, 1800);
        await this.wait(200);
      },
    ];
  }

  // ---- scene ops -------------------------------------------------------

  private showPanel(name: PanelName, kb: KenBurns): void {
    for (const [n, p] of this.panels) {
      if (n === name) {
        p.classList.remove('kb-a', 'kb-b', 'kb-c');
        p.classList.add('show', `kb-${kb}`);
      } else {
        p.classList.remove('show');
      }
    }
  }

  /** Rune band sweeps across; the panel swaps under it at the midpoint. */
  private async runeWipe(name: PanelName, kb: KenBurns): Promise<void> {
    this.audio.sfx.play(SfxId.RuneWipe, 0.9);
    this.audio.music.introPulse(0.35);
    this.wipe.classList.remove('run');
    void this.wipe.offsetWidth;
    this.wipe.classList.add('run');
    await this.wait(260);
    this.showPanel(name, kb);
    await this.wait(300);
  }

  private setKicker(label: string, kind: 'eyebrow' | 'plate' = 'eyebrow'): void {
    let kicker = this.text.querySelector<HTMLParagraphElement>('.cine-kicker');
    if (!kicker) {
      kicker = el('p', 'cine-kicker');
      this.text.prepend(kicker);
    }
    kicker.textContent = label;
    kicker.classList.toggle('plate', kind === 'plate');
    kicker.classList.remove('in');
    void kicker.offsetWidth;
    kicker.classList.add('in');
  }

  private clearText(): void {
    this.text.replaceChildren();
  }

  /**
   * Types the lore block while the matching narration plays. Dwells until the
   * voice finishes (plus a breath), so pacing tracks the spoken line rather
   * than a fixed timer. Captions stay off — the crawl text is the reading surface.
   */
  private async crawlNarrated(lines: readonly string[], voiceGroup: string): Promise<void> {
    const prevCaption = this.audio.announcer.onCaption;
    this.audio.announcer.onCaption = null;
    const said = this.say(voiceGroup);
    try {
      const block = el('div', 'cine-crawl', this.text);
      for (let i = 0; i < lines.length; i++) {
        if (this.skipped) return;
        const p = el('p', 'typing', block);
        const caret = el('i', 'caret', p);
        const line = lines[i];
        for (let c = 0; c < line.length; c++) {
          if (this.skipped) return;
          caret.insertAdjacentText('beforebegin', line[c]);
          await this.wait(TYPE_MS_PER_CHAR);
        }
        p.classList.remove('typing');
        caret.remove();
        if (i < lines.length - 1) await this.wait(CRAWL_LINE_HOLD_MS);
      }
      // Hold for the clip (or a readable fallback), then a short breath before the wipe.
      await this.waitVoice(said, said ? 400 : LORE_FALLBACK_HOLD_MS, LORE_VOICE_MAX_MS);
      await this.wait(LORE_BREATH_MS);
    } finally {
      this.audio.announcer.onCaption = prevCaption;
    }
  }

  private showTitle(): void {
    this.clearText();
    const card = el('div', 'cine-title', this.text);
    const presents = el('p', 'presents', card);
    presents.textContent = 'THE ARCANE BROADCAST PRESENTS';
    const h1 = el('h1', '', card);
    h1.textContent = 'WIZARD ARENA';
    const h2 = el('h2', '', card);
    h2.textContent = 'TOTAL MAYHEM';
    this.root.classList.remove('punch');
    void this.root.offsetWidth;
    this.root.classList.add('punch');
  }

  /** Slams the four stakes lines in on the drumroll; `alreadyMs` is roll time spent before the card mounted. */
  private async stakesCard(alreadyMs = 0): Promise<void> {
    const card = el('div', 'cine-stakes', this.text);
    let elapsed = alreadyMs;
    for (let i = 0; i < STAKES.length; i++) {
      await this.wait(STAKE_SLAM_MS[i] - elapsed);
      elapsed = Math.max(elapsed, STAKE_SLAM_MS[i]);
      if (this.skipped) return;
      const line = el('p', i === STAKES.length - 1 ? 'slam win' : 'slam', card);
      line.textContent = STAKES[i];
      if (i < STAKES.length - 1) this.audio.sfx.play(SfxId.GateSlam, 0.45);
      else this.doFlash(0.55);
    }
    await this.wait(300);
  }

  private doFlash(strength = 1): void {
    this.flash.style.setProperty('--flash', String(strength));
    this.flash.classList.remove('go');
    void this.flash.offsetWidth;
    this.flash.classList.add('go');
  }

  private showHint(): void {
    this.hint.classList.add('show');
  }

  private showCaption(text: string, ms: number): void {
    this.captionText.textContent = text;
    this.caption.classList.add('show');
    window.clearTimeout(this.captionTimer);
    this.captionTimer = window.setTimeout(() => this.caption.classList.remove('show'), Math.max(600, ms - 250));
  }

  // ---- voice -------------------------------------------------------------

  private say(group: string): boolean {
    return this.audio.announcer.say(group);
  }

  /** Hold at least `minMs`, then until Vaelen stops talking (or the ceiling). Missing clips just hold. */
  private async waitVoice(said: boolean, minMs: number, maxMs = VOICE_MAX_MS): Promise<void> {
    const started = performance.now();
    await this.wait(minMs);
    if (!said) return;
    while (!this.skipped && this.audio.announcer.isSpeaking() && performance.now() - started < maxMs) {
      await this.wait(60);
    }
  }

  // ---- timing / input ----------------------------------------------------

  private wait(ms: number): Promise<void> {
    if (this.skipped || ms <= 0) return Promise.resolve();
    return new Promise<void>((resolve) => {
      const id = window.setTimeout(() => {
        this.timers.delete(id);
        const at = this.waiters.indexOf(resolve);
        if (at >= 0) this.waiters.splice(at, 1);
        resolve();
      }, ms);
      this.timers.add(id);
      this.waiters.push(resolve);
    });
  }

  private tick = (): void => {
    if (this.finished) return;
    this.audio.announcer.update();
    this.pollGamepads();
    this.raf = requestAnimationFrame(this.tick);
  };

  private pollGamepads(): void {
    let pads: ReadonlyArray<Gamepad | null>;
    try {
      pads = navigator.getGamepads ? navigator.getGamepads() : [];
    } catch {
      return;
    }
    let pressed = false;
    const now: boolean[] = [];
    for (const pad of pads) {
      if (!pad) continue;
      for (let b = 0; b < pad.buttons.length; b++) {
        const down = pad.buttons[b].pressed;
        const key = pad.index * 32 + b;
        now[key] = down;
        if (down && !this.padButtons[key]) pressed = true;
      }
    }
    this.padButtons = now;
    if (pressed && this.padArmed) this.skip();
  }

  private armSkip(): void {
    if (this.finished) return;
    this.padArmed = true;
    window.addEventListener('keydown', this.onKey);
    window.addEventListener('pointerdown', this.onPointer);
  }

  private onKey = (e: KeyboardEvent): void => {
    if (e.repeat || e.metaKey || e.ctrlKey) return;
    if (e.code.startsWith('F') && e.code.length <= 3) return;
    if (e.key === 'Shift' || e.key === 'Alt' || e.key === 'Control' || e.key === 'Meta') return;
    e.preventDefault();
    this.skip();
  };

  private onPointer = (): void => {
    this.skip();
  };

  private skip(): void {
    if (this.skipped || this.finished) return;
    this.skipped = true;
    this.audio.announcer.interrupt();
    this.audio.music.setIntroBuild(1);
    for (const id of this.timers) window.clearTimeout(id);
    this.timers.clear();
    const pending = this.waiters.splice(0);
    for (const resolve of pending) resolve();
  }

  private finish(): void {
    if (this.finished) return;
    this.finished = true;
    writeSeen();
    cancelAnimationFrame(this.raf);
    window.removeEventListener('keydown', this.onKey);
    window.removeEventListener('pointerdown', this.onPointer);
    window.clearTimeout(this.captionTimer);
    this.audio.announcer.onCaption = this.prevCaption;
    this.root.classList.add('out');
    const fade = this.skipped ? 140 : 320;
    window.setTimeout(() => this.root.remove(), fade + 60);
    // Hand off as the fade begins so the slam is not waiting on a fade-out.
    const done = this.resolveDone;
    this.resolveDone = null;
    window.setTimeout(() => done?.(), this.skipped ? 0 : 120);
  }
}

export function playOpeningCinematic(opts: OpeningCinematicOptions): Promise<void> {
  return new OpeningCinematic(opts).play();
}
